import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import { Settings } from "../../../settings";
import { buildGambleScatterConfig } from "./gambleScatterConfig";
import { plusSkinForCount } from "../Mystery/goldenMysteryConfig";

/**
 * The `+FS` scatter's reveal — the free-spins twin of the gamble scatter's.
 *
 * ── Same window, different strip ──
 *
 * Vladimir, 2026-09-18, after watching the reference game: the additional-FS
 * scatters never just land. The cell lands the GOLDEN SCATTER, its doors open,
 * and a strip of `+1 / +2 / +3` spins inside that window until it stops on the
 * one the round awarded.
 *
 * That is the gamble scatter's sequence exactly — land closed, `reveal`, loop
 * `anticipation`, settle on `revealed_static` — so this config is built FROM
 * that one and replaces only the strip. Doors, sounds, timings and poses stay
 * shared, which is the point: two reveals that look like one mechanism because
 * they are one mechanism.
 *
 * GDD §7 says the bonus game replaces SC and SC1 with the additional-FS
 * scatters. It never says the replacement inherits SC1's reveal; the reference
 * does, and the payload allows it — the server sends `[coord, "16", fs_count]`,
 * so the answer is known before the doors open, exactly as it is for the
 * gamble scatter's `[coord, "12", fs_count, fs_level]`.
 */

/** Every count the strip can show, and the only ones the server sends. */
export const PLUS_FS_COUNTS = [1, 2, 3];

/**
 * The pool, which is the whole set.
 *
 * Unlike the gamble strip there is nothing to narrow: three outcomes, all
 * reachable, none better or worse to tease with. So `buildItems` is turned OFF
 * below and this fixed list is what spins, which also guarantees the awarded
 * one is always in the pool.
 */
export const PLUS_STRIP_ITEMS = PLUS_FS_COUNTS.map((fsCount) => ({ fsCount }));

/**
 * An empty strip cell: one sprite, reused across renders.
 *
 * No text object, unlike the gamble card. The `+N` is ART on the symbol —
 * `bs`'s `add_1/2/3` skins each carry their own number and its shadow — so
 * there is nothing for the client to draw or to keep in step with a font size.
 */
const createCell = () => {
  const cell = new PIXI.Container();
  cell.name = "plus_scatter_cell";

  const sprite = new PIXI.Sprite();
  sprite.anchor.set(0.5);
  sprite.name = "plus_scatter_art";
  // Whole pixels while moving, the same rule the reels and the Golden Mystery
  // strip both apply: a sprite travelling at a fractional speed is otherwise
  // resampled every frame, which reads as shimmer on the digits.
  sprite.roundPixels = true;
  cell.addChild(sprite);

  cell.__art = sprite;
  return cell;
};

/**
 * Draw one `+N` into a cell.
 *
 * Size is a PARAMETER and never captured — the spinner passes its current cell
 * size on every draw, so a resize cannot leave the strip out of step with the
 * spine that replaces it.
 */
const renderCell = (cell, item, cellWidth, cellHeight) => {
  const sprite = cell?.__art;
  if (!sprite) return;

  const skin = plusSkinForCount(item?.fsCount);
  const texture = skin
    ? SpineTextureExtractor.getSkinTexture("bs", skin)
    : null;

  sprite.texture = texture || PIXI.Texture.EMPTY;
  // EXACTLY the rule the reels use — cellHeight / SYMBOL_HEIGHT, with no
  // reference to the texture's own size. Fitting to the texture makes the
  // spinning symbol a different size from the one that lands, because a
  // captured frame includes effect art that extends past the symbol.
  sprite.scale.set(cellHeight / Settings.SYMBOL_HEIGHT);
};

/** Aliases PIXI.Assets holds the `bs` rig under. */
const SPINE_NAME = "bs";

/**
 * PlusScatterOverlay — the live `+N` that replaces the landed strip texture.
 *
 * The free-spins twin of GambleLevelOverlay, and deliberately the same shape:
 * the strip spins flat textures, and the moment one lands it becomes a real
 * skeleton so it can animate. Every other landing in this game does that — the
 * reels through OverlayReelController, the gamble card through its own
 * overlay — and this cell was the one that did not.
 *
 * What it adds over the texture is one thing: `bounce`. The rig carries three
 * clips — `bounce`, `distribute`, `static` — and `bounce` scales the `amount`
 * bone 0.7 → 1.1 → 0.9 → 1 over about two thirds of a second. `amount` is the
 * bone every `scatter3_N` attachment hangs off, so the number squashes and
 * springs back exactly as a landed symbol does on the reels.
 *
 * ── The skin is applied ALONE, never combined ──
 *
 * Unlike GambleLevelOverlay, which layers its colour over `default`. The two
 * rigs are built differently: `bs`'s `default` skin is the symbol's OWN closed
 * doors, eclipse and glows, and `add_1/2/3` carry nothing but the number and
 * its shadow. Combining them here would put a second pair of doors inside the
 * host's window, which already has its own.
 *
 * Applying `add_N` alone is also what makes the swap invisible:
 * SpineTextureExtractor captured the flat strip cell with `setSkinByName`, the
 * same single skin, so the texture and this skeleton are the same pixels.
 * `bounce` still keys the eclipse and glow slots, but with no attachments in
 * this skin they key nothing, and only the number moves.
 */
class PlusScatterOverlay {
  /**
   * @param {object}         p
   * @param {PIXI.Container} p.parent     The reveal's `fit` container.
   * @param {object}         p.item       `{fsCount}` the strip landed on.
   * @param {number}         p.cellHeight
   */
  constructor({ parent, item, cellHeight }) {
    this.parent = parent;
    this.item = item;
    this.cellHeight = cellHeight;
    this.spine = null;
    this.destroyed = false;
    this._build();
  }

  _build() {
    const skin = plusSkinForCount(this.item?.fsCount);
    // A count the rig has no skin for is not a cell — leave the strip's own
    // texture standing rather than mounting an empty skeleton over it.
    if (!skin) return;

    try {
      this.spine = Spine.from(`${SPINE_NAME}-skeleton`, `${SPINE_NAME}-atlas`);
    } catch (error) {
      console.warn("[PlusScatterOverlay] Could not create spine:", error);
      return;
    }
    if (!this.spine) return;

    this.spine.name = "plus_scatter_card";
    try {
      this.spine.skeleton.setSkinByName(skin);
      // Slots only. setToSetupPose resets BONES as well, which undoes the
      // first frame the landing clip is about to apply.
      this.spine.skeleton.setSlotsToSetupPose();
    } catch (error) {
      console.warn("[PlusScatterOverlay] Unknown skin:", skin, error);
    }

    this._applyScale();
    this.parent.addChild(this.spine);
    this._playLanding();
  }

  /**
   * EXACTLY the strip cell's rule — `cellHeight / Settings.SYMBOL_HEIGHT`.
   *
   * Not similar, the same, and for the reason GambleLevelOverlay gives at
   * length: this spine replaces a texture that was captured FROM it at scale
   * 1, so the two line up only when both are drawn at the same factor. Fitting
   * to the skeleton's bounds instead would size it by whatever effect art
   * happens to be posed, and the swap would jump.
   */
  _applyScale() {
    if (this.spine) this.spine.scale.set(this.cellHeight / Settings.SYMBOL_HEIGHT);
  }

  /**
   * `bounce`, then `static` held for ever — the reels' land-then-idle order.
   *
   * ── Unconditional, and that is the house rule ──
   *
   * Vladimir, 2026-09-18: "if the animation has a bounce, then it should play
   * it — it is a rule of thumb." So there is no arrival flag here and no test
   * for which of RevealSymbolController's three onLanded callers is asking.
   * A cell that appears plays its landing, the same as the gamble card beside
   * it and the same as every symbol on the reels.
   *
   * The rig decides, not the caller: no `bounce` in the skeleton and this is a
   * no-op, so a re-export that drops or renames the clip degrades to the
   * resting card rather than warning.
   *
   * `static` keys nothing at all, which is what makes it the resting look: the
   * skeleton simply stays where `bounce` left it. Looped, because a zero-key
   * one-shot ends on the frame it starts and Spine reverts to the setup pose.
   *
   * The state is updated and applied before anything is drawn, the same guard
   * showLandingAnimation uses on the reels — without it the first rendered
   * frame is the setup pose rather than the clip's own start.
   */
  _playLanding() {
    const state = this.spine?.state;
    if (!state) return;
    const animations = this.spine.skeleton?.data?.animations || [];
    const has = (name) => animations.some((a) => a.name === name);
    if (!has("bounce")) return;

    state.setAnimation(0, "bounce", false);
    if (has("static")) state.addAnimation(0, "static", true, 0);
    state.update(0);
    state.apply(this.spine.skeleton);
  }

  resize(cellWidth, cellHeight) {
    if (this.destroyed || !cellHeight) return;
    this.cellHeight = cellHeight;
    this._applyScale();
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.spine) {
      try {
        this.spine.state?.clearListeners?.();
        this.parent?.removeChild?.(this.spine);
        this.spine.destroy();
      } catch {
        // Already gone with its parent.
      }
      this.spine = null;
    }
  }
}

/**
 * The config handed to RevealSymbolController.
 *
 * Everything but the strip comes from the gamble scatter's config, so the two
 * reveals cannot drift: one set of door clips, one set of sounds, one set of
 * timings.
 */
export const buildPlusScatterConfig = (cellWidth, cellHeight) => {
  const base = buildGambleScatterConfig(cellWidth, cellHeight);
  return {
    ...base,

    // ── SHORTER than the gamble scatter's 2600 ──
    //
    // That number is tuned for the moment that decides the whole bonus, and it
    // is also half hidden: the Golden Mystery's own track spins 2.6s with the
    // first second behind the `myg.reveal` lid, so the player sees about 1.6s
    // of it. This window has no lid — it is visible from the frame the doors
    // part — so the same 2600 read as much longer than anything around it.
    //
    // 1800 is Vladimir's number, 2026-09-18, and it puts the visible spin
    // alongside the mystery's own visible 1.6s rather than on top of it.
    //
    // `settleOnLoopComplete` is inherited and still adds up to 0.5s on the
    // anticipation loop's boundary, which is deliberate — the swap to the
    // settled pose happens on a frame the loop was going to render anyway.
    spinDurationMs: 1800,

    // ── NOT skippable, unlike the gamble scatter's ──
    //
    // Vladimir, 2026-09-18. This window opens while the Golden Mystery around
    // it is still running: the other cells keep spinning, and this one is one
    // more thing on a busy board rather than the thing the player is waiting
    // for. A press there means "get on with the round", and answering it by
    // slamming only THIS strip — while its neighbours carry on — reads as a
    // fault. So the press passes it by and the reveal plays out on its own.
    //
    // The gamble scatter keeps its skip. That reveal has the screen to itself
    // and the press is the player asking for their answer sooner, which is
    // what GDD §13 describes.
    //
    // Only the WAIT is affected. The strip still lands on the count the round
    // awarded, at the same moment it would have anyway.
    spinSkippable: false,

    // ── The landed cell becomes a LIVE spine, like every other landing ──
    //
    // This was null until 2026-09-18, and the reason it was null is worth
    // keeping: the gamble config's own hook builds a LEVEL CARD, and handed a
    // `{fsCount}` item it found no tier, built nothing, and then hid the strip
    // a frame later — which is what made the landed +1 disappear. Inheriting
    // the hook was the fault; having no hook was only the workaround.
    //
    // Vladimir, same day: "i want them all to match, its the same thing
    // regardless of what lands". So this is the gamble card's mechanism with
    // the right rig in it — see PlusScatterOverlay above.
    //
    // Spine FIRST, texture hidden ONE FRAME LATER, which is the order every
    // landing in this engine uses (useLandingAnimations says why: the GPU
    // upload needs the frame, and the overlap is invisible because both show
    // the same picture). Hiding in the same frame leaves one frame with
    // neither on screen, and that frame is the flash.
    onLanded: ({ parent, item, cellHeight, strip }) => {
      const overlay = new PlusScatterOverlay({ parent, item, cellHeight });
      // Only when there is something to hide behind — a skin the rig does not
      // have leaves `spine` null, and the strip's texture is then the only
      // picture there is.
      if (!overlay.spine) return overlay;
      requestAnimationFrame(() => {
        if (strip?.container && !strip.destroyed) {
          strip.container.visible = false;
        }
      });
      return overlay;
    },

    strip: {
      ...base.strip,

      // ── ITS OWN MOTION, pinned, because the base config's moved ──
      //
      // On 2026-09-22 the gamble scatter's strip was unified with the Golden
      // Mystery's — speed 170 -> 130, overshoot 1.25 -> 0, `stopMs` dropped in
      // favour of a derived landing, and a real wind-up. That was asked for,
      // and only for the BASE GAME's scatter.
      //
      // This config spreads `base.strip`, so it inherited all of it silently.
      // Worse, the same day fixed `_injectStrip`, which until then copied four
      // keys and dropped the rest: `windUpDistance` and `landingExitSpeed` had
      // never reached the spinner from ANY config, so this window gained a
      // wind-up and a new landing it has never had in its life — in the bonus
      // room, unwatched.
      //
      // So the numbers it actually ran on are written down here. They are the
      // four that used to get through, and they are what this reveal was tuned
      // and signed off with on 2026-09-18.
      //
      // Delete this block to unify the two — but watch it in the room first.
      speedMs: 170,
      accelMs: 320,
      stopMs: 560,
      overshoot: 1.25,
      // Both OFF, which is what "never reached the spinner" means in practice.
      windUpDistance: 0,
      landingExitSpeed: null,

      items: PLUS_STRIP_ITEMS,
      // ── Both explicitly OFF, and that is not tidying ──
      //
      // They are spread in from the gamble config above, and both are
      // gamble-shaped: `buildItems` reads `result.tier` and returns the landed
      // rung and everything above it, `openItem` filters the blank out of the
      // opening frame. Left in place they would be handed a `{fsCount}` item,
      // find no tier, and quietly hand the strip the wrong pool.
      //
      // Null rather than deleted because the controller reads them with
      // optional chaining — `buildItems?.(result) || items` — so null falls
      // through to the fixed list above.
      buildItems: null,
      openItem: null,
      createCell,
      renderCell,
    },
  };
};

/**
 * Turn what the round carries into the item the strip lands on.
 *
 * The cell's payload is `[coord, "16", fs_count]`, which the staging has
 * already turned into `{symbolId, fsCount}`. A bare number is accepted too, so
 * a caller with nothing but the count can still ask for a reveal.
 *
 * @param   {{fsCount:number}|number} result
 * @returns {{fsCount:number}|null} null when the count is not one the rig has
 *   a skin for, which leaves the reveal unplayed rather than landing on
 *   nothing.
 */
export const toStripItem = (result) => {
  const fsCount = Number(
    typeof result === "number" ? result : result?.fsCount,
  );
  return PLUS_FS_COUNTS.includes(fsCount) ? { fsCount } : null;
};

/**
 * What the reel must hold once the reveal has settled.
 *
 * Null: the Golden Mystery paints every settled cell itself from the board's
 * FINAL state, after the whole loop has run — see the block below
 * runGoldenMysteryLoop. A picture written here would be overwritten a moment
 * later by that pass, and on a chained round it would be the wrong one.
 */
export const toStaticCell = () => null;
